/* ═══════════════════════════════════════════════════════════════════════════
   Aurora Health · MindLumina 3D product
   Photoreal real-time bottle + carton, built to the real print artwork.

   Geometry is measured from the supplied product photography and dieline:
     bottle   Ø41.5mm, 101mm tall with cap, 56ml
     carton   50 × 115 × 50mm, four 470×1084px panels
     label    2400×900 wrap, applied at true aspect so type never stretches

   Textures come straight from Aurora's print PDFs, so the label and every
   carton panel read as the actual product, not an approximation.

   Usage:
     import { mountProduct } from './assets/3d/aurora-product-3d.js';
     mountProduct({ el: document.querySelector('#viz'), mode: 'both' });

   Options:
     el          required host element, sized by CSS
     mode        'bottle' | 'box' | 'both'          default 'both'
     autoRotate  boolean                            default true
     spin        idle turntable speed, rad/sec      default 0.18
     drag        allow the visitor to orbit         default true
     env         'studio' | 'soft'                  default 'studio'
     onReady     callback once the first frame is drawn
   ═══════════════════════════════════════════════════════════════════════════ */

import * as THREE from './three.module.js';
import { OrbitControls } from './OrbitControls.js';

const BASE = new URL('.', import.meta.url).href;
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── real product dimensions, millimetres ─────────────────────────────────
   Not estimated. The carton dieline fixes the scale (panel 470×1084px, so
   width = height/2.306), and the bottle silhouette below is traced off the
   product photograph at 25px intervals against that scale. The label height
   independently confirms it: circumference/2.667 (the texture aspect) lands
   on 55.1mm, and the photo measures 54.9mm.                                */
const MM = {
  bodyR: 23.2,
  neckR: 15.8,
  collarY: 72.6, collarR: 18.9, collarH: 3.0,
  capR: 18.0, capBottom: 76.0, capH: 25.2,
  labelR: 23.4, labelBottom: 2.8,
  box: { w: 49.9, h: 115, d: 49.9 }
};

/* traced shoulder, (radius, height) straight off the photograph */
const SHOULDER = [
  [23.20, 58.5], [23.06, 60.2], [22.22, 62.1], [21.08, 64.0],
  [20.01, 65.9], [18.86, 67.8], [17.64, 69.7], [16.40, 71.0], [15.80, 72.0]
];

/* ── Silver Skin Spray, 100 ml ────────────────────────────────────────────
   Traced the same way, off AURORA SILVER SKIN SPRAY 100ML (FRONT).png at
   6.8 px/mm. The scale is confirmed by volume: a 34.7mm bore over a 103mm
   straight body is 97ml, which is the 100ml fill.                          */
/* Measured off the 100ml studio shot: body 270 x 715px, white collar 130px,
   clear over-cap 210px. That is a 1:2.65 body, which at a 100ml fill is 36.4mm
   across by 96mm tall. The body was never really the problem: what made the old
   model read as a lab pipette was the cap, 12.8mm on a 34.7mm bottle. On the
   real bottle the collar and cap are close to the full width of the body. */
const SS = {
  bodyR: 18.2, bodyH: 96, shoulderTop: 100,
  collarR: 15.5, collarTop: 117.5,
  /* The over-cap is a spray cap, not a pipette: on the real bottle it sits
     close to the collar diameter and is stubby. Narrow and tall reads dropper. */
  capBaseR: 15.0, capR: 14.0, capTop: 146,
  /* The decal is sized FROM the artwork rather than guessed. Its height is
     derived from the wrapped arc length so the print keeps its true aspect:
     the old fixed 5 to 96 span squeezed a 1.36:1 image onto a 0.35:1 surface,
     which is what warped the lettering. */
  decalArc: 150 * Math.PI / 180,
  decalAspect: 1200 / 880,   // ss-decal.png, width over height
  decalCentreY: 46           // printed across the middle of the body
};

/* ── does this browser do WebGL at all ───────────────────────────────────── */
function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) { return false; }
}

/* ── a studio environment built in code, so there is no HDRI to download ───
   Bright softbox rectangles on a dark room. This is what gives the glass its
   moving highlight streaks: a flat gradient environment cannot do that.      */
function buildEnvScene(kind) {
  const s = new THREE.Scene();
  s.background = new THREE.Color(kind === 'soft' ? 0x2a2e33 : 0x14171b);

  const panel = (w, h, intensity, pos, look) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    m.material.color.multiplyScalar(intensity);
    m.position.set(...pos);
    m.lookAt(...(look || [0, 0, 0]));
    s.add(m);
    return m;
  };

  /* A real product table: two tall strip softboxes raking the bottle from
     both front quarters, a hard kicker behind the shoulder, and a wide
     overhead. Narrow and bright, never broad and dim: a wide dim panel washes
     dark glass to mid-grey, a narrow bright one gives the tight vertical
     streak that reads as glass. */
  var K = kind === 'soft' ? 0.52 : 0.7;

  /* Contrast comes from the RATIO between these, not their absolute size.
     A big soft overhead lifts every face at once, which is exactly what
     flattens printed navy to grey, so the ambient is kept deliberately low
     and the shaping is done by two narrow, bright strips. */

  // key strip, high left, close: the long highlight running down the bottle
  panel(3.0, 20, 30 * K, [-7.2, 5.5, 7]);
  // its tighter neighbour, for the second highlight on the shoulder curve
  panel(1.5, 13, 17 * K, [-4.6, 8.5, 8.5]);
  // right strip, deliberately weak, so the form turns instead of going flat
  panel(2.4, 17, 4.5 * K, [8.4, 4.5, 6.5]);
  // hard kicker from behind right: the bright rim that separates dark glass
  panel(1.3, 11, 9 * K, [7.6, 7, -6.5]);
  // back fill, just enough to lift the silhouette off the ground
  panel(12, 9, 2.0 * K, [0, 5, -11]);
  // faint bounce from below so the base does not crush to pure black
  panel(14, 9, 0.5, [0, -7, 3]);
  // overhead: low. This is the one that was washing the printed panels out.
  panel(22, 22, kind === 'soft' ? 0.85 : 0.4, [0, 13, 0], [0, 0, 0]);

  return s;
}

/* ── brushed aluminium finish ──────────────────────────────────────────────
   A single flat roughness value is what makes CG metal read as plastic: real
   anodised aluminium is covered in fine vertical grain, so its highlight breaks
   up into streaks instead of sitting as one clean band. This paints that grain
   into a roughness map, plus a matching faint normal map for the tooling marks. */
let _brushed = null;
function brushedMetal() {
  if (_brushed) return _brushed;
  const W = 64, H = 1024;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');

  // base roughness, mid grey
  g.fillStyle = '#8a8a8a';
  g.fillRect(0, 0, W, H);

  // vertical grain: many faint 1px columns of varying darkness and length
  for (let i = 0; i < 1400; i++) {
    const x = Math.random() * W;
    const y = Math.random() * H;
    const len = 40 + Math.random() * 420;
    const v = Math.random();
    g.strokeStyle = 'rgba(' + (v > 0.5 ? '255,255,255,' : '0,0,0,') +
                    (0.03 + Math.random() * 0.10) + ')';
    g.lineWidth = Math.random() < 0.75 ? 1 : 2;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x, y + len);
    g.stroke();
  }

  const rough = new THREE.CanvasTexture(c);
  rough.wrapS = rough.wrapT = THREE.RepeatWrapping;
  rough.repeat.set(6, 1);          // wrap the grain several times round the can
  rough.anisotropy = 8;

  _brushed = rough;
  return _brushed;
}

/* Fine moulding noise. Injection-moulded caps and glass are never optically
   perfect: a little roughness variation stops the highlight from being one
   flawless CG band, which is most of what reads as "cartoony". */
let _grain = null;
function mouldGrain() {
  if (_grain) return _grain;
  const S = 256;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d');
  const img = g.createImageData(S, S);
  for (let i = 0; i < S * S; i++) {
    // gentle noise around mid grey, biased so most of the surface stays smooth
    const n = 128 + (Math.random() - 0.5) * 46 + (Math.random() < 0.02 ? 28 : 0);
    img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = n;
    img.data[i * 4 + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(3, 3);
  t.anisotropy = 4;
  _grain = t;
  return _grain;
}

/* ── soft contact shadow, a blurred radial on a plane ─────────────────────── */
function contactShadow(radius, opacity) {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, `rgba(0,0,0,${opacity})`);
  grad.addColorStop(0.45, `rgba(0,0,0,${opacity * 0.55})`);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(radius * 2, radius * 2),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = -1;
  return mesh;
}

/* ── the bottle ───────────────────────────────────────────────────────────── */
function buildBottle(tex) {
  const g = new THREE.Group();
  const V = (x, y) => new THREE.Vector2(x, y);

  /* glass silhouette: base fillet, straight body, traced shoulder, collar, neck */
  const pts = [];
  pts.push(V(0, 0.35));
  pts.push(V(MM.bodyR - 2.8, 0));
  pts.push(V(MM.bodyR - 1.0, 0.4));
  pts.push(V(MM.bodyR, 1.7));
  SHOULDER.forEach(([r, y]) => pts.push(V(r, y)));
  // moulded collar flange, which sits proud of the cap wall on the real bottle
  pts.push(V(MM.collarR, MM.collarY));
  pts.push(V(MM.collarR, MM.collarY + MM.collarH));
  pts.push(V(MM.neckR + 0.4, MM.collarY + MM.collarH + 0.7));
  pts.push(V(MM.neckR + 0.4, MM.capBottom + MM.capH - 2));
  pts.push(V(0, MM.capBottom + MM.capH - 2));

  const glassGeo = new THREE.LatheGeometry(pts, 128);
  glassGeo.computeVertexNormals();

  /* Dark amber glass carrying a near-black botanical extract. The product
     reads opaque, so this is a very dark, very glossy dielectric rather than a
     transmissive shell: transmission over an opaque fill only costs frames. */
  const glass = new THREE.Mesh(glassGeo, new THREE.MeshPhysicalMaterial({
    /* Amber glass over a near-black fill. A hint of warmth beats pure black,
       and the moulding grain keeps the highlight from being mirror-perfect. */
    color: 0x0C0908,
    metalness: 0,
    roughness: 0.11,
    roughnessMap: mouldGrain(),
    clearcoat: 1,
    clearcoatRoughness: 0.06,
    ior: 1.52,
    reflectivity: 0.66,
    envMapIntensity: 0.86,
    // just enough warm sheen to hint at amber glass at the grazing edges
    sheen: 0.1,
    sheenColor: new THREE.Color(0x2a1a08),
    sheenRoughness: 0.55
  }));
  glass.castShadow = true;
  g.add(glass);

  /* wrap label, at the texture's own aspect so nothing stretches */
  const labR = MM.labelR;
  const labH = (2 * Math.PI * labR) / (2400 / 900);
  const labGeo = new THREE.CylinderGeometry(labR, labR, labH, 160, 1, true);
  const label = new THREE.Mesh(labGeo, new THREE.MeshPhysicalMaterial({
    map: tex.label,
    roughness: 0.52,
    metalness: 0,
    clearcoat: 0.5,
    clearcoatRoughness: 0.3,
    envMapIntensity: 0.6
  }));
  label.position.y = MM.labelBottom + labH / 2;
  /* The wrap is one continuous print, so the front artwork is not at the
     seam: the MindLumina lockup sits at u=0.370 across the 2400px file.
     CylinderGeometry puts u=0 at +Z, so rotate that panel to camera.       */
  label.rotation.y = -0.370 * Math.PI * 2;
  g.add(label);

  /* white ribbed screw cap: flutes are displaced radially on a lathe */
  const capH = MM.capH;
  /* traced cap: straight fluted wall, then a flat-ish crown that rounds off
     over the last 4mm, which is what the photograph shows */
  const capPts = [
    V(0, 0), V(MM.capR - 1.3, 0), V(MM.capR, 1.0),
    V(MM.capR, 20.5),
    V(17.8, 21.8), V(17.2, 22.8), V(16.0, 23.8),
    V(14.0, 24.5), V(11.0, 24.9), V(6.0, 25.1), V(0, capH)
  ];

  /* 8 segments per rib. At 192 segments the 52 flutes fall below Nyquist and
     computeVertexNormals smooths them into a plain cylinder. */
  const RIBS = 52, AMP = 0.75, SEG = RIBS * 8;
  const capGeo = new THREE.LatheGeometry(capPts, SEG);
  const pos = capGeo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const r = Math.hypot(x, z);
    // only flute the straight wall, leave the crown and the base fillet smooth
    if (r > MM.capR - 1.4 && y > 1.0 && y < 20.5) {
      const theta = Math.atan2(z, x);
      const d = AMP * (0.5 + 0.5 * Math.cos(RIBS * theta));
      const k = (r + d) / r;
      pos.setX(i, x * k);
      pos.setZ(i, z * k);
    }
  }
  capGeo.computeVertexNormals();

  /* HDPE screw cap: satin, not glossy. The grain map is what stops it reading
     as a smooth plastic toy. */
  const cap = new THREE.Mesh(capGeo, new THREE.MeshPhysicalMaterial({
    color: 0xF4F4F1,
    metalness: 0,
    roughness: 0.46,
    roughnessMap: mouldGrain(),
    clearcoat: 0.4,
    clearcoatRoughness: 0.42,
    envMapIntensity: 0.66
  }));
  cap.position.y = MM.capBottom;
  cap.castShadow = true;
  g.add(cap);

  return g;
}

/* ── the Silver Skin Spray bottle ─────────────────────────────────────────
   Anodised aluminium body, white fluted collar, clear tapered over-cap. The
   print is a DECAL on a partial cylinder rather than a full wrap, because it
   is screen-printed on the metal and only covers the front arc.            */
function buildSpray(tex, finish) {
  const g = new THREE.Group();
  const V = (x, y) => new THREE.Vector2(x, y);

  /* body: straight bore, tight base fillet, shoulder into the neck */
  const pts = [
    V(0, 0.3), V(SS.bodyR - 2.2, 0), V(SS.bodyR - 0.7, 0.35), V(SS.bodyR, 1.4),
    V(SS.bodyR, SS.bodyH)
  ];
  for (let i = 1; i <= 10; i++) {
    const t = i / 10;
    pts.push(V(
      SS.bodyR + (SS.collarR - SS.bodyR) * Math.sin(t * Math.PI * 0.5),
      SS.bodyH + (SS.shoulderTop - SS.bodyH) * (1 - Math.cos(t * Math.PI * 0.5))
    ));
  }
  pts.push(V(SS.collarR, SS.collarTop), V(0, SS.collarTop));

  const bodyGeo = new THREE.LatheGeometry(pts, 128);
  bodyGeo.computeVertexNormals();
  /* Anodised aluminium: fully metallic, with the brush grain driving roughness
     so the highlight breaks into streaks. A touch of clearcoat keeps the
     lacquer sheen without the waxy plastic look a high clearcoat gives. */
  /* A fully metallic surface has no diffuse term, so in a dark studio it just
     mirrors the room and reads near-black. Real anodised aluminium is a light
     satin silver, so metalness is held back a little to let the base colour
     carry, and the brush grain drives roughness so the highlight breaks up
     into streaks rather than one clean mirror band. */
  const bodyMat = finish === 'white'
    ? new THREE.MeshPhysicalMaterial({
        color: 0xF6F8F8, metalness: 0, roughness: 0.32,
        clearcoat: 0.85, clearcoatRoughness: 0.28, envMapIntensity: 0.75
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0xD3D7DB,
        metalness: 0.62,
        roughness: 0.38,
        roughnessMap: brushedMetal(),
        envMapIntensity: 0.8,
        clearcoat: 0.14,
        clearcoatRoughness: 0.55
      });
  g.add(new THREE.Mesh(bodyGeo, bodyMat));

  /* white fluted collar, same displacement trick as the MindLumina cap */
  const RIBS = 38, AMP = 0.30, SEG = RIBS * 8;
  const colGeo = new THREE.LatheGeometry([
    V(0, 0), V(SS.collarR - 0.8, 0), V(SS.collarR, 0.7),
    V(SS.collarR, 13.2), V(SS.collarR - 0.9, 14.2), V(SS.capBaseR, 14.8), V(0, 14.8)
  ], SEG);
  const cp = colGeo.attributes.position;
  for (let i = 0; i < cp.count; i++) {
    const x = cp.getX(i), y = cp.getY(i), z = cp.getZ(i);
    const r = Math.hypot(x, z);
    if (r > SS.collarR - 1.0 && y > 0.7 && y < 13.2) {
      const k = (r + AMP * (0.5 + 0.5 * Math.cos(RIBS * Math.atan2(z, x)))) / r;
      cp.setX(i, x * k); cp.setZ(i, z * k);
    }
  }
  colGeo.computeVertexNormals();
  const collar = new THREE.Mesh(colGeo, new THREE.MeshPhysicalMaterial({
    color: 0xF6F6F4, metalness: 0, roughness: 0.36,
    clearcoat: 1, clearcoatRoughness: 0.22, envMapIntensity: 0.72
  }));
  collar.position.y = SS.shoulderTop;
  g.add(collar);

  /* clear tapered over-cap. Genuine transmission here: it is the one part of
     the whole set that is actually see-through, and it is what sells it. */
  /* Near straight-sided with a domed crown. An aggressive taper here turns
     the spray cap into something that reads as a dropper pipette. */
  const h = SS.capTop - SS.collarTop;
  const capPts = [
    V(0, 0), V(SS.capBaseR - 0.5, 0), V(SS.capBaseR, 0.6),
    V(SS.capBaseR, h * 0.52), V(SS.capR + 0.15, h * 0.84)
  ];
  for (let i = 1; i <= 8; i++) {
    const t = i / 8;                         // quarter-round crown
    capPts.push(V(SS.capR * Math.cos(t * Math.PI / 2),
                  h * 0.84 + (h * 0.16) * Math.sin(t * Math.PI / 2)));
  }
  const cap = new THREE.Mesh(new THREE.LatheGeometry(capPts, 96),
    new THREE.MeshPhysicalMaterial({
      color: 0xFFFFFF, metalness: 0, roughness: 0.09,
      transmission: 0.92, thickness: 1.6, ior: 1.49,
      clearcoat: 1, clearcoatRoughness: 0.05,
      envMapIntensity: 0.9, transparent: true, opacity: 1
    }));
  cap.position.y = SS.collarTop;
  g.add(cap);

  /* the printed decal: a partial cylinder hugging the front arc */
  if (tex.ssDecal) {
    /* height follows from the wrap, so the artwork can never be squeezed:
       arc length / aspect = the height that keeps the print square-on */
    const dr = SS.bodyR + 0.14;
    const arcLen = dr * SS.decalArc;
    const dh = arcLen / SS.decalAspect;
    const dGeo = new THREE.CylinderGeometry(
      dr, dr, dh, 96, 1, true,
      -SS.decalArc / 2, SS.decalArc
    );
    const decal = new THREE.Mesh(dGeo, new THREE.MeshPhysicalMaterial({
      map: tex.ssDecal, transparent: true, roughness: 0.62, metalness: 0,
      envMapIntensity: 0.22, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2
    }));
    decal.position.y = SS.decalCentreY;
    g.add(decal);
  }
  return g;
}

/* ── squeeze tubes and pump bottles ───────────────────────────────────────
   Proportions traced from the product photographs the same way the bottle
   and spray were: silhouette bounds measured against the white ground, then
   a width profile sampled at ten heights to read the form.

   Tubes are flattened, not round. A lathe gives a circle, so the group is
   scaled on Z to bring the cross-section back to the oval a real tube has.
   Without that the volume comes out three times the fill it is sold in.   */
const TUBE = {
  /* Facial Cleanser and Moisture Booster, 60 ml.
     Silhouette 705x1773 (aspect 0.398); width profile 98 -> 61 top to bottom,
     so the crimp is the widest point and it stands on its cap. */
  gp60: { crimpR: 34, baseR: 21.5, h: 132, flat: 0.56,
          capR: 21.5, capH: 30, crimpH: 8, decalTop: 0.92, decalBottom: 0.14 },
  /* Imimate, 15 g. Photographed lying down: 475x189, so length:diameter 2.5. */
  imimate: { crimpR: 23, baseR: 14.5, h: 90, flat: 0.54,
             capR: 14.5, capH: 21, crimpH: 6, decalTop: 0.92, decalBottom: 0.14 }
};

const PUMP = {
  /* Body Wash, 260 ml. Silhouette 543x1853 (aspect 0.293); the profile jumps
     30, 45, then 88 and holds, so the top sixth is pump head over a straight
     body. A 27mm bore over a 130mm body is 297ml, which is the 260ml fill
     plus headroom. */
  bodyR: 27, bodyH: 130, shoulderH: 18, neckR: 15,
  headH: 44, spoutR: 5.5, spoutDrop: 10,
  decalCentreFrac: 0.46, decalArc: 150 * Math.PI / 180
};

/* ── a squeeze tube ───────────────────────────────────────────────────────
   Built upright on its cap, which is how all four are photographed: crimped
   seal at the top, shoulder down to a screw cap at the bottom. */
function buildTube(tex, cfg, decal) {
  const g = new THREE.Group();
  const V = (x, y) => new THREE.Vector2(x, y);
  const c = cfg;

  /* body profile, cap at y=0 upward to the crimp */
  const pts = [V(0, c.capH)];
  pts.push(V(c.baseR - 1.2, c.capH), V(c.baseR, c.capH + 1.6));
  const bodyTop = c.capH + c.h;
  for (let i = 0; i <= 16; i++) {
    const t = i / 16;
    /* a gentle swell: real tubes are fullest just under the crimp */
    pts.push(V(c.baseR + (c.crimpR - c.baseR) * Math.pow(t, 0.82),
               c.capH + c.h * t));
  }
  /* the crimp itself: a flat pinched seal */
  pts.push(V(c.crimpR, bodyTop + c.crimpH * 0.55),
           V(c.crimpR * 0.97, bodyTop + c.crimpH),
           V(0, bodyTop + c.crimpH));

  const bodyGeo = new THREE.LatheGeometry(pts, 128);
  bodyGeo.computeVertexNormals();
  g.add(new THREE.Mesh(bodyGeo, new THREE.MeshPhysicalMaterial({
    color: 0xF7F8F7, metalness: 0, roughness: 0.42,
    clearcoat: 0.55, clearcoatRoughness: 0.4, envMapIntensity: 0.7
  })));

  /* screw cap, lightly fluted like the others in the range */
  const RIBS = 40, AMP = 0.22, SEG = RIBS * 6;
  const capGeo = new THREE.LatheGeometry([
    V(0, 0), V(c.capR - 1.0, 0), V(c.capR, 1.0),
    V(c.capR, c.capH - 1.2), V(c.capR - 0.8, c.capH), V(0, c.capH)
  ], SEG);
  const cp = capGeo.attributes.position;
  for (let i = 0; i < cp.count; i++) {
    const x = cp.getX(i), y = cp.getY(i), z = cp.getZ(i);
    const r = Math.hypot(x, z);
    if (r > c.capR - 0.9 && y > 1.0 && y < c.capH - 1.2) {
      const k = (r + AMP * (0.5 + 0.5 * Math.cos(RIBS * Math.atan2(z, x)))) / r;
      cp.setX(i, x * k); cp.setZ(i, z * k);
    }
  }
  capGeo.computeVertexNormals();
  g.add(new THREE.Mesh(capGeo, new THREE.MeshPhysicalMaterial({
    color: 0xF2F3F1, metalness: 0, roughness: 0.34,
    clearcoat: 1, clearcoatRoughness: 0.2, envMapIntensity: 0.72
  })));

  /* the print, hugging the front arc between the shoulder and the crimp */
  if (decal) {
    const y0 = c.capH + c.h * c.decalBottom;
    const y1 = c.capH + c.h * c.decalTop;
    const dh = y1 - y0;
    const dr = (c.baseR + c.crimpR) / 2 + 0.14;
    const arc = 152 * Math.PI / 180;
    const dGeo = new THREE.CylinderGeometry(dr, dr, dh, 96, 1, true, -arc / 2, arc);
    const m = new THREE.Mesh(dGeo, new THREE.MeshPhysicalMaterial({
      map: decal, transparent: true, roughness: 0.6, metalness: 0,
      envMapIntensity: 0.22, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2
    }));
    m.position.y = (y0 + y1) / 2;
    g.add(m);
  }

  /* flatten the round lathe into the oval a tube actually is */
  g.scale.z = c.flat;
  return g;
}

/* ── a pump bottle ────────────────────────────────────────────────────────
   Straight body, rolled shoulder, ribbed collar and a pump head with a
   down-turned spout. */
function buildPump(tex, decal) {
  const g = new THREE.Group();
  const V = (x, y) => new THREE.Vector2(x, y);
  const P = PUMP;

  const pts = [V(0, 0.4), V(P.bodyR - 2.4, 0), V(P.bodyR - 0.8, 0.4), V(P.bodyR, 1.8),
               V(P.bodyR, P.bodyH)];
  for (let i = 1; i <= 12; i++) {
    const t = i / 12;
    pts.push(V(P.bodyR + (P.neckR - P.bodyR) * Math.sin(t * Math.PI * 0.5),
               P.bodyH + P.shoulderH * (1 - Math.cos(t * Math.PI * 0.5))));
  }
  const neckTop = P.bodyH + P.shoulderH;
  pts.push(V(P.neckR, neckTop + 5), V(0, neckTop + 5));

  const bodyGeo = new THREE.LatheGeometry(pts, 128);
  bodyGeo.computeVertexNormals();
  g.add(new THREE.Mesh(bodyGeo, new THREE.MeshPhysicalMaterial({
    color: 0xF6F8F8, metalness: 0, roughness: 0.34,
    clearcoat: 0.7, clearcoatRoughness: 0.3, envMapIntensity: 0.75
  })));

  /* ribbed collar */
  const RIBS = 46, AMP = 0.24, SEG = RIBS * 6;
  const colGeo = new THREE.LatheGeometry([
    V(0, 0), V(P.neckR + 1.4, 0), V(P.neckR + 2.0, 0.8),
    V(P.neckR + 2.0, 12), V(P.neckR + 1.0, 13), V(0, 13)
  ], SEG);
  const cp = colGeo.attributes.position;
  for (let i = 0; i < cp.count; i++) {
    const x = cp.getX(i), y = cp.getY(i), z = cp.getZ(i);
    const r = Math.hypot(x, z);
    if (r > P.neckR + 1.1 && y > 0.8 && y < 12) {
      const k = (r + AMP * (0.5 + 0.5 * Math.cos(RIBS * Math.atan2(z, x)))) / r;
      cp.setX(i, x * k); cp.setZ(i, z * k);
    }
  }
  colGeo.computeVertexNormals();
  const collar = new THREE.Mesh(colGeo, new THREE.MeshPhysicalMaterial({
    color: 0xEFF1F0, metalness: 0, roughness: 0.32,
    clearcoat: 1, clearcoatRoughness: 0.2, envMapIntensity: 0.7
  }));
  collar.position.y = neckTop;
  g.add(collar);

  /* pump head: a stem, then the pressed cap and its spout */
  const white = new THREE.MeshPhysicalMaterial({
    color: 0xF4F5F4, metalness: 0, roughness: 0.3,
    clearcoat: 1, clearcoatRoughness: 0.18, envMapIntensity: 0.72
  });
  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(P.spoutR + 1.4, P.spoutR + 1.4, P.headH * 0.52, 48), white);
  stem.position.y = neckTop + 13 + P.headH * 0.26;
  g.add(stem);

  const head = new THREE.Mesh(new THREE.LatheGeometry([
    V(0, 0), V(P.spoutR + 4.6, 0), V(P.spoutR + 5.2, 1.0),
    V(P.spoutR + 5.2, 8.4), V(P.spoutR + 3.2, 10.2), V(0, 10.2)
  ], 64), white);
  head.position.y = neckTop + 13 + P.headH * 0.52;
  g.add(head);

  /* the spout, angled forward and down */
  const spout = new THREE.Mesh(
    new THREE.CylinderGeometry(P.spoutR, P.spoutR * 0.92, 17, 40), white);
  spout.rotation.z = Math.PI / 2;
  spout.rotation.y = 0;
  spout.position.set(9.5, neckTop + 13 + P.headH * 0.52 + 5.4, 0);
  g.add(spout);
  const lip = new THREE.Mesh(
    new THREE.CylinderGeometry(P.spoutR * 0.94, P.spoutR * 0.94, 5.5, 36), white);
  lip.position.set(17.6, neckTop + 13 + P.headH * 0.52 + 1.8, 0);
  g.add(lip);

  if (decal) {
    const dr = P.bodyR + 0.14;
    const arcLen = dr * P.decalArc;
    const img = decal.image;
    const aspect = img ? img.width / img.height : 0.36;
    const dh = arcLen / aspect;
    const dGeo = new THREE.CylinderGeometry(dr, dr, Math.min(dh, P.bodyH * 0.92),
      96, 1, true, -P.decalArc / 2, P.decalArc);
    const m = new THREE.Mesh(dGeo, new THREE.MeshPhysicalMaterial({
      map: decal, transparent: true, roughness: 0.6, metalness: 0,
      envMapIntensity: 0.22, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2
    }));
    m.position.y = P.bodyH * P.decalCentreFrac;
    g.add(m);
  }
  return g;
}

/* ── the carton ───────────────────────────────────────────────────────────── */
function buildBox(tex) {
  const { w, h, d } = MM.box;
  const geo = new THREE.BoxGeometry(w, h, d);

  const paper = (map) => new THREE.MeshPhysicalMaterial({
    map, roughness: 0.66, metalness: 0,
    clearcoat: 0.14, clearcoatRoughness: 0.72,
    envMapIntensity: 0.5
  });

  // BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z
  const mats = [
    paper(tex.boxRight), paper(tex.boxLeft),
    paper(tex.boxTop),   paper(tex.boxBottom),
    paper(tex.boxFront), paper(tex.boxBack)
  ];

  const m = new THREE.Mesh(geo, mats);
  m.castShadow = true;
  return m;
}

/* ── loader ──────────────────────────────────────────────────────────────── */
function loadTextures(renderer) {
  const loader = new THREE.TextureLoader();
  const maxAniso = renderer.capabilities.getMaxAnisotropy();

  const get = (file, wrap) => new Promise((res) => {
    loader.load(BASE + file, (t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = maxAniso;
      if (wrap) { t.wrapS = THREE.RepeatWrapping; }
      t.flipY = true;
      res(t);
    }, undefined, () => res(null));
  });

  // plain white for the unprinted underside of the carton
  const white = () => {
    const c = document.createElement('canvas');
    c.width = c.height = 8;
    const g = c.getContext('2d');
    g.fillStyle = '#f6f5f2';
    g.fillRect(0, 0, 8, 8);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };

  return Promise.all([
    get('ml-label.png', true),
    get('ml-box-front.png'), get('ml-box-back.png'),
    get('ml-box-left.png'), get('ml-box-right.png'),
    get('ml-box-top.png'),
    get('ss-decal.png'),
    get('fc-decal.webp'), get('mb-decal.webp'),
    get('bw-decal.webp'), get('em-decal.webp'), get('im-decal.webp')
  ]).then(([label, f, b, l, r, t, ss, fc, mb, bw, em, im]) => ({
    label, boxFront: f, boxBack: b, boxLeft: l, boxRight: r,
    boxTop: t, boxBottom: white(), ssDecal: ss,
    fcDecal: fc, mbDecal: mb, bwDecal: bw, emDecal: em, imDecal: im
  }));
}

/* ── mount ───────────────────────────────────────────────────────────────── */
export function mountProduct(opts) {
  const el = opts && opts.el;
  if (!el) return null;

  const mode = opts.mode || 'both';
  const wantSpin = opts.autoRotate !== false && !REDUCED;
  const spin = opts.spin != null ? opts.spin : 0.18;
  const allowDrag = opts.drag !== false;

  if (!hasWebGL()) { el.setAttribute('data-3d', 'unsupported'); return null; }

  let renderer, scene, camera, controls, group, raf = null, running = false;
  let bounds = null, spinR = 0;

  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // PBR Neutral, not ACES: ACES washes the navy label toward grey
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = 0.62;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    el.appendChild(renderer.domElement);
    Object.assign(renderer.domElement.style, { width: '100%', height: '100%', display: 'block' });
  } catch (e) {
    el.setAttribute('data-3d', 'failed');
    return null;
  }

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(28, 1, 1, 2000);

  /* image-based lighting via PMREM, never a raw equirect */
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envRT = pmrem.fromScene(buildEnvScene(opts.env || 'studio'), 0.04);
  scene.environment = envRT.texture;

  /* one directional light purely for the cast shadow and a crisp edge */
  const key = new THREE.DirectionalLight(0xffffff, 0.58);
  key.position.set(-70, 130, 90);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.near = 20;
  key.shadow.camera.far = 420;
  key.shadow.camera.left = -110;
  key.shadow.camera.right = 110;
  key.shadow.camera.top = 130;
  key.shadow.camera.bottom = -60;
  key.shadow.bias = -0.0012;
  key.shadow.radius = 3;
  scene.add(key);

  group = new THREE.Group();
  scene.add(group);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.075;
  controls.enablePan = false;
  controls.enableZoom = false;
  controls.enabled = allowDrag;
  controls.rotateSpeed = 0.7;
  controls.minPolarAngle = Math.PI * 0.22;
  controls.maxPolarAngle = Math.PI * 0.62;

  let userTouched = false;
  controls.addEventListener('start', () => { userTouched = true; });

  loadTextures(renderer).then((tex) => {
    const shadowY = 0.2;

    /* every product in the range now has a model; the id picks the form */
    const TUBES = {
      imimate:   { cfg: TUBE.imimate, decal: 'imDecal',   shadow: 20 },
      cleanser:  { cfg: TUBE.gp60,    decal: 'fcDecal',   shadow: 26 },
      booster:   { cfg: TUBE.gp60,    decal: 'mbDecal',   shadow: 26 }
    };
    if (TUBES[opts.product]) {
      const t = TUBES[opts.product];
      group.add(buildTube(tex, t.cfg, t.decal ? tex[t.decal] : null));
      const sh = contactShadow(t.shadow, 0.6);
      sh.position.y = shadowY;
      group.add(sh);
    } else if (opts.product === 'bodywash') {
      group.add(buildPump(tex, tex.bwDecal));
      const sh = contactShadow(30, 0.62);
      sh.position.y = shadowY;
      group.add(sh);
    } else if (opts.product === 'essencemist') {
      /* the mist is the same spray form, slimmer and with its own print */
      const mist = buildSpray({ ssDecal: tex.emDecal }, 'white');
      mist.scale.set(0.92, 1.02, 0.92);
      group.add(mist);
      const sh = contactShadow(24, 0.6);
      sh.position.y = shadowY;
      group.add(sh);
    } else if (opts.product === 'silverspray') {
      const spray = buildSpray(tex);
      group.add(spray);
      const sh = contactShadow(26, 0.6);
      sh.position.y = shadowY;
      group.add(sh);
    } else {
    if (mode === 'bottle' || mode === 'both') {
      const bottle = buildBottle(tex);
      // close enough to read as one product shot, as in Aurora's own photograph
      bottle.position.x = (mode === 'both') ? 29 : 0;
      group.add(bottle);
      const sh = contactShadow(30, 0.66);
      sh.position.set(bottle.position.x, shadowY, 0);
      group.add(sh);
    }
    if (mode === 'box' || mode === 'both') {
      const box = buildBox(tex);
      box.position.y = MM.box.h / 2;
      box.position.x = (mode === 'both') ? -26 : 0;
      // a carton is never perfectly square to camera in a real shot
      box.rotation.y = (mode === 'both') ? 0.22 : 0.16;
      group.add(box);
      const sh = contactShadow(36, 0.62);
      sh.position.set(box.position.x, shadowY, 0);
      group.add(sh);
    }
    }

    /* Camera sits square to the product so 'front' really is front; the
       three-quarter look comes from turning the GROUP, which keeps every
       named view honest. Bounds are measured AFTER that turn, because a
       rotated pair projects wider than the axis-aligned one. */
    group.rotation.y = opts.angle != null ? opts.angle : -0.34;
    group.updateMatrixWorld(true);

    const bb = new THREE.Box3().setFromObject(group);
    const c = bb.getCenter(new THREE.Vector3());
    const sz = bb.getSize(new THREE.Vector3());
    group.position.x -= c.x;
    group.position.z -= c.z;
    bounds = sz;

    /* The group turns about Y, so its worst-case on-screen width is the
       radius it sweeps, not its axis-aligned width. Measure that from the
       real corners rather than assuming. */
    spinR = 0;
    const c8 = [bb.min, bb.max];
    [c8[0].x, c8[1].x].forEach(function (x) {
      [c8[0].z, c8[1].z].forEach(function (z) {
        spinR = Math.max(spinR, Math.hypot(x - c.x, z - c.z));
      });
    });

    frame();
    render();
    if (opts.onReady) opts.onReady();
    el.setAttribute('data-3d', 'ready');
  });

  /* ── sizing: only ever size against real dimensions ───────────────────── */
  function fit() {
    const w = el.clientWidth, h = el.clientHeight;
    if (!w || !h) return false;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    return true;
  }

  /* Fit each axis exactly.
     A bounding-SPHERE fit is tilt-proof but brutally wasteful here: the pair
     is ~120 wide and ~115 tall, which gives a sphere over 200 across, so the
     camera ends up ~1.5x too far and the product reads tiny in a big empty
     stage. Instead fit the real height, and for width use the radius the group
     sweeps as it spins about Y, which is the true worst case for a turntable.
     The downward tilt is added into the height term rather than paid for with
     a sphere. */
  function frame() {
    if (!fit() || !bounds) return;
    const pad = opts.pad != null ? opts.pad : 1.04;
    const lift = opts.lift != null ? opts.lift : 0.10;

    const vFov = camera.fov * Math.PI / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);

    // the tilt raises the camera, which widens the vertical extent by 2 x lift
    const effH = bounds.y * (1 + 2 * lift) * pad;
    const effW = 2 * spinR * pad;

    const dist = Math.max(
      effH / (2 * Math.tan(vFov / 2)),
      effW / (2 * Math.tan(hFov / 2))
    );

    const midY = bounds.y * 0.5;
    controls.target.set(0, midY, 0);
    // a few degrees of downward tilt, so the contact shadow is not edge-on
    camera.position.set(0, midY + bounds.y * lift, dist);
    camera.lookAt(controls.target);
    controls.update();
  }

  const ro = new ResizeObserver(() => { frame(); render(); });
  ro.observe(el);

  /* ── frame loop, gated on visibility so an off-screen hero costs nothing ── */
  let last = performance.now(), t = 0;
  function render() { renderer.render(scene, camera); }

  /* Idle motion is a SWAY, not a turntable.
     A continuous rotation guarantees that at some point the customer is
     looking at the back of the pack, which on a landing page is the one thing
     the product must never do. Swaying around the presentation angle keeps
     the printed front to camera at all times and still feels alive. Drag
     still gives a full 360. */
  const baseAngle = () => (opts.angle != null ? opts.angle : -0.34);
  const sway = opts.sway != null ? opts.sway : 0.30;   // radians either side

  function tick(now) {
    raf = requestAnimationFrame(tick);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now; t += dt;
    if (wantSpin && !userTouched) {
      if (opts.motion === 'turntable') group.rotation.y += spin * dt;
      else group.rotation.y = baseAngle() + Math.sin(t * spin * 2.2) * sway;
    }
    controls.update();
    render();
  }

  function start() {
    if (running) return;
    running = true; last = performance.now();
    raf = requestAnimationFrame(tick);
  }
  function stop() {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    raf = null;
  }

  const io = new IntersectionObserver((entries) => {
    entries[0].isIntersecting ? start() : stop();
  }, { threshold: 0.01 });
  io.observe(el);

  // reduced motion still gets a real render, just a still one
  if (REDUCED) { stop(); }

  return {
    el,
    /* jump to a named angle, so a page can present the product from several
       sides without a second scene */
    view(name, ms) {
      const angles = {
        front: 0, back: Math.PI, left: Math.PI * 0.5, right: -Math.PI * 0.5,
        threequarter: -0.62, hero: -0.38
      };
      const to = angles[name] != null ? angles[name] : 0;
      userTouched = true;
      const from = group.rotation.y;
      const dur = ms == null ? 700 : ms;
      const t0 = performance.now();
      const step = (t) => {
        const k = Math.min((t - t0) / dur, 1);
        const e = 1 - Math.pow(1 - k, 3);
        group.rotation.y = from + (to - from) * e;
        render();
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    resume() { userTouched = false; },
    destroy() {
      stop(); io.disconnect(); ro.disconnect();
      controls.dispose(); envRT.dispose(); pmrem.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.remove();
    }
  };
}

export default mountProduct;
